import { toast } from "@/core/notifications";

import { CustomerIoAdapter } from "../CustomerIoAdapter/CustomerIoAdapter";
import { SegmentAdapter } from "../SegmentAdapter/SegmentAdapter";
import { AnalyticsEvents, AnalyticsTraits, IAnalyticsAdapter } from "../types";

export class AnalyticsService implements IAnalyticsAdapter {
  private readonly adapters: IAnalyticsAdapter[];

  constructor(...adapters: IAnalyticsAdapter[]) {
    this.adapters = adapters;
  }

  trackEvent<Event extends keyof AnalyticsEvents>(
    event: Event,
    properties: AnalyticsEvents[Event],
  ): void {
    this.adapters.forEach((adapter) => {
      adapter.trackEvent(event, properties);
    });
  }

  trackPage(name: string): void {
    this.adapters.forEach((adapter) => {
      adapter.trackPage(name);
    });
  }

  identify(userId: string, traits: AnalyticsTraits): void {
    this.adapters.forEach((adapter) => {
      adapter.identify(userId, traits);
    });
  }

  /**
   * Clear identity on all adapters that support it. Called on logout so that
   * Customer.io in-app messages targeted at the logged-out user do not leak
   * into the next admin's session.
   */
  reset(): void {
    this.adapters.forEach((adapter) => {
      adapter.reset?.();
    });
  }
}

/**
 * Name of the DOM CustomEvent emitted when a Customer.io in-app message
 * action asks for an in-app navigation. A React-Router-aware listener
 * component (see `modules/adminInbox/CustomerIoNavigationListener.tsx`)
 * consumes this event and calls `navigate(path)` so the navigation stays
 * inside the SPA instead of causing a full page reload.
 */
export const CUSTOMER_IO_NAVIGATE_EVENT = "customerio:navigate";

export type CustomerIoNavigateEventDetail = { readonly path: string };

/**
 * Handles well-known `actionName`/`actionValue` pairs emitted by Customer.io
 * in-app messages. Called by the CustomerIoAdapter when the SDK fires
 * `in-app:message-action`.
 *
 * Known actions:
 *  - `show_toast`: value = toast message text.
 *  - `navigate`: value = admin-relative path (must start with `/admin/`).
 *    Dispatches a `customerio:navigate` DOM event that the SPA router
 *    listener converts into an in-place `navigate(path)` call.
 *  - `open_url`: value = absolute https:// URL; opens in a new tab.
 *
 * Unknown actions are logged and ignored so that a new action added on the
 * Customer.io side does not hard-fail the frontend.
 */
export const handleCustomerIoMessageAction = (actionName: string, actionValue: string): void => {
  if (actionName === "show_toast") {
    const sanitizedMessage = getSafeCustomerIoToastMessage(actionValue);
    if (!sanitizedMessage) {
      return;
    }

    toast.success(sanitizedMessage);
    globalThis.dispatchEvent(new Event("ageless-communication-received"));
    return;
  }

  if (actionName === "navigate") {
    const safePath = getSafeAdminNavigationPath(actionValue);
    if (!safePath) {
      console.warn("Customer.io navigate action rejected invalid path:", actionValue);
      return;
    }

    globalThis.dispatchEvent(
      new CustomEvent<CustomerIoNavigateEventDetail>(CUSTOMER_IO_NAVIGATE_EVENT, {
        detail: { path: safePath },
      }),
    );
    return;
  }

  if (actionName === "open_url") {
    if (!isSafeHttpsUrl(actionValue)) {
      console.warn("Customer.io open_url action rejected non-https URL:", actionValue);
      return;
    }

    globalThis.open(actionValue, "_blank", "noopener,noreferrer");
    return;
  }

  console.warn(`Customer.io message action '${actionName}' is not handled`);
};

const MAX_CUSTOMER_IO_TOAST_LENGTH = 200;

const getSafeCustomerIoToastMessage = (message: string): string | null => {
  if (!message) {
    return null;
  }

  const withControlCharactersNormalized = Array.from(message)
    .map((character) => {
      const charCode = character.charCodeAt(0);
      return charCode < 32 || charCode === 127 ? " " : character;
    })
    .join("");

  const sanitized = withControlCharactersNormalized
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_CUSTOMER_IO_TOAST_LENGTH);

  return sanitized || null;
};

const getSafeAdminNavigationPath = (path: string): string | null => {
  if (!path) return null;

  // Must be a same-origin admin path. Reject protocol-based URLs and
  // protocol-relative URLs (//evil.example.com) that the leading `/admin`
  // check by itself cannot catch.
  if (path !== "/admin" && !path.startsWith("/admin/")) {
    return null;
  }

  // Block any path that embeds a protocol-relative segment after the /admin
  // prefix, e.g. `/admin//evil.example.com/steal`, which some browsers will
  // honor as an origin change when handed to window.location / URL parsing.
  // We use URL parsing against a stable base so reserved characters / ../
  // sequences are fully normalized before comparison.
  try {
    const url = new URL(path, "https://admin.local");
    if (url.origin !== "https://admin.local") return null;
    if (!url.pathname.startsWith("/admin")) return null;
    // Reject any double-slash within the path (e.g. /admin//evil.example.com)
    // because browsers happily treat those as origin changes in certain
    // contexts. URL normalization alone preserves the double slash, so we
    // scan the pathname explicitly.
    if (url.pathname.includes("//")) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

const isSafeHttpsUrl = (value: string): boolean => {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:";
  } catch {
    return false;
  }
};

const segmentAdapter = new SegmentAdapter(import.meta.env.VITE_SEGMENT_WRITE_KEY);
segmentAdapter.initialize();

export const customerIoAdapter = new CustomerIoAdapter(
  import.meta.env.REACT_APP_CUSTOMERIO_WRITE_KEY,
  import.meta.env.REACT_APP_CUSTOMERIO_SITE_ID,
  {
    onMessageAction: handleCustomerIoMessageAction,
    segmentTrack: (eventName, properties) => segmentAdapter.trackEventByName(eventName, properties),
  },
);
customerIoAdapter.initialize();

const Analytics = new AnalyticsService(segmentAdapter, customerIoAdapter);

export default Analytics;
